import React, { useEffect, useMemo, useState } from 'react';
import blogService, { BlogPost } from '../../services/blogService';
import RichTextEditor from './RichTextEditor';
import ImageUpload, { ProductImage } from './ImageUpload';
import './FloristBlog.css';

const defaultForm: Partial<BlogPost> = {
  title: '',
  slug: '',
  short_description: '',
  content: '',
  hero_image: '',
  seo_title: '',
  seo_description: '',
  seo_keywords: '',
  status: 'draft'
};

const FloristBlog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [form, setForm] = useState<Partial<BlogPost>>(defaultForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [heroImages, setHeroImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await blogService.getAll();
      setPosts(data);
    } catch (err) {
      console.error(err);
      setError('Kunde inte ladda blogginlägg');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name === 'title' && !editingId) {
      setForm(prev => ({ ...prev, slug: slugify(value) }));
    }
  };

  const slugify = (text: string) =>
    text
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || '';

  const updateHeroFieldFromImages = (images: ProductImage[]) => {
    const uploadedHero = images.find(img => !img.uploading && img.url && !img.url.startsWith('data:'));
    setForm(prev => ({ ...prev, hero_image: uploadedHero?.url || '' }));
  };

  const handleHeroImagesChange = (next: ProductImage[] | ((prev: ProductImage[]) => ProductImage[])) => {
    if (typeof next === 'function') {
      setHeroImages(prev => {
        const computed = next(prev);
        updateHeroFieldFromImages(computed);
        return computed;
      });
    } else {
      setHeroImages(next);
      updateHeroFieldFromImages(next);
    }
  };

  const syncHeroImageFromUrl = (url?: string, title?: string) => {
    if (url) {
      setHeroImages([{
        url,
        alt_text: title || 'Hero-bild',
        is_primary: true,
        uploading: false
      }]);
    } else {
      setHeroImages([]);
    }
  };

  const resetFormState = () => {
    setForm(defaultForm);
    setEditingId(null);
    setHeroImages([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form } as Partial<BlogPost>;
      if (!payload.title) throw new Error('Titel saknas');
      payload.slug = payload.slug || slugify(payload.title);
      if (editingId) {
        await blogService.update(editingId, payload);
        setMessage('Bloggpost uppdaterad');
      } else {
        await blogService.create(payload);
        setMessage('Bloggpost skapad');
      }
      resetFormState();
      await loadPosts();
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Något gick fel');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (post: BlogPost) => {
    setEditingId(post.id);
    setForm(post);
    syncHeroImageFromUrl(post.hero_image, post.title);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Ta bort bloggposten?')) return;
    try {
      await blogService.delete(id);
      setMessage('Bloggpost raderad');
      await loadPosts();
    } catch (err) {
      console.error(err);
      setError('Kunde inte ta bort bloggpost');
    }
  };

  const sortedPosts = useMemo(
    () => [...posts].sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()),
    [posts]
  );

  return (
    <div className="florist-blog-admin">
      <div>
        <h2>{editingId ? 'Redigera bloggpost' : 'Skapa bloggpost'}</h2>
        {message && <div className="admin-success">{message}</div>}
        {error && <div className="admin-error">{error}</div>}
        <form className="blog-form" onSubmit={handleSubmit}>
          <div>
            <label>Titel</label>
            <input name="title" value={form.title || ''} onChange={handleInput} required />
          </div>
          <div>
            <label>Slug</label>
            <input name="slug" value={form.slug || ''} onChange={handleInput} required />
          </div>
          <div>
            <label>Kort beskrivning</label>
            <textarea name="short_description" value={form.short_description || ''} onChange={handleInput} />
          </div>
          <div>
            <ImageUpload
              images={heroImages}
              onImagesChange={handleHeroImagesChange}
              maxImages={1}
              label="Hero-bild"
              entityLabel="inlägg"
            />
            <small style={{ color: 'var(--color-text-light)' }}>
              Lägg till en bred hero-bild. Den används som topp-bild på bloggen och i social delning.
            </small>
          </div>
          <div>
            <label>Innehåll</label>
            <RichTextEditor value={form.content || ''} onChange={(html) => setForm(prev => ({ ...prev, content: html }))} />
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
            <div>
              <label>SEO-titel</label>
              <input name="seo_title" value={form.seo_title || ''} onChange={handleInput} />
            </div>
            <div>
              <label>SEO-beskrivning</label>
              <input name="seo_description" value={form.seo_description || ''} onChange={handleInput} />
            </div>
            <div>
              <label>SEO-nyckelord</label>
              <input name="seo_keywords" value={form.seo_keywords || ''} onChange={handleInput} placeholder="kommaseparerade" />
            </div>
          </div>
          <div>
            <label>Status</label>
            <select name="status" value={form.status || 'draft'} onChange={handleInput}>
              <option value="draft">Utkast</option>
              <option value="published">Publicerad</option>
            </select>
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Sparar...' : editingId ? 'Uppdatera' : 'Publicera'}
          </button>
          {editingId && (
            <button type="button" className="btn btn-secondary" onClick={() => resetFormState()}>
              Avbryt
            </button>
          )}
        </form>
      </div>

      <section className="blog-table">
        <h3 style={{ padding: '1rem' }}>Blogginlägg</h3>
        {loading ? (
          <div style={{ padding: '1rem' }}>Laddar...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Titel</th>
                <th>Status</th>
                <th>Publicerad</th>
                <th>Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {sortedPosts.map(post => (
                <tr key={post.id}>
                  <td>{post.title}</td>
                  <td><span className={`status-pill ${post.status}`}>{post.status === 'published' ? 'Publicerad' : 'Utkast'}</span></td>
                  <td>{post.published_at ? new Date(post.published_at).toLocaleDateString('sv-SE') : '-'}</td>
                  <td className="blog-actions">
                    <button type="button" onClick={() => handleEdit(post)}>Redigera</button>
                    <button type="button" onClick={() => handleDelete(post.id)}>Ta bort</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default FloristBlog;
